import { DrawerAccordionSection, scrollToElement } from '@diana/core';
import React, { useCallback } from 'react';
import { ClickAwayListener, TextField, Tooltip } from '@mui/material';
import { IEntitySectionProps } from './EntitySection.types';
import { useEntitySectionActions } from './useEntitySectionActions';
import { useTranslation } from 'react-i18next';
import { THING_PAGE_NS } from '../../../../i18n';
import { useSectionRename } from '../../../../../../hooks/useSectionRename';

export function EntitySection({
  component,
  workId,
  step,
  color,
  index,
  work,
  closeDrawer,
}: IEntitySectionProps) {
  const { t } = useTranslation(THING_PAGE_NS);

  const {
    isFirst,
    isMobileDrawer,
    isEditMode,
    isValueValid,
    borderRadius,
    onEnterPress,
    onDoubleClick,
    handleSectionRenaming,
    handleChange,
    value,
    children,
    containerStyles,
  } = useSectionRename({
    component,
    workId,
    work,
    step,
    index,
    useSectionActions: useEntitySectionActions,
  });

  const editor = (
    <ClickAwayListener mouseEvent="onMouseUp" onClickAway={handleSectionRenaming}>
      <Tooltip open={!isValueValid} placement="top" title={t('section.notAllowedCharacters')}>
        <TextField
          variant="filled"
          size="small"
          hiddenLabel
          fullWidth
          value={value}
          onChange={handleChange}
          autoComplete="off"
          onKeyUp={onEnterPress}
          autoFocus
          sx={{
            top: 6,
            ml: 2,
            '.MuiFilledInput-root': {
              borderRadius: `${borderRadius}px`,
              height: 32,
              p: 0,
              pl: 0.5,
              '> input': { pl: '10px', pt: '9px' },
            },
          }}
          InputProps={{
            disableUnderline: true,
          }}
        />
      </Tooltip>
    </ClickAwayListener>
  );

  const onTitleClick = useCallback(
    (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
      scrollToElement({ e, id: component.id });
      isMobileDrawer && closeDrawer();
    },
    [closeDrawer, component.id, isMobileDrawer],
  );

  return component?.name ? (
    <DrawerAccordionSection
      color={color}
      onTitleClick={onTitleClick}
      onPanelDoubleClick={onDoubleClick}
      label={value}
      editor={editor}
      isEditMode={isEditMode}
      isFirst={isFirst}
      sx={{ ...containerStyles, cursor: component.canEditLabel ? 'text' : 'arrow' }}
    >
      {children}
    </DrawerAccordionSection>
  ) : null;
}
